import { fetchAPI } from './fetchAPI';

// ==================== GenAIOps Traces & Sessions ====================

export const getOverviewMetrics = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();

  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/overview/metrics${query ? '?' + query : ''}`);
};

export const getTraces = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.userId || params.user_id) queryParams.append('user_id', params.userId || params.user_id);
  if (params.sessionId || params.session_id) queryParams.append('session_id', params.sessionId || params.session_id);
  if (params.tags) params.tags.forEach((tag: string) => queryParams.append('tags', tag));

  // Status and outcome filters
  if (params.status) queryParams.append('status', params.status);
  if (params.outcome) queryParams.append('outcome', params.outcome);

  // Agent filter
  if (params.agentName || params.agent_name) queryParams.append('agent_name', params.agentName || params.agent_name);

  // Date range filters
  if (params.startDate || params.start_date) queryParams.append('start_date', params.startDate || params.start_date);
  if (params.endDate || params.end_date) queryParams.append('end_date', params.endDate || params.end_date);

  // Sorting
  if (params.sortBy || params.sort_by) queryParams.append('sort_by', params.sortBy || params.sort_by);
  if (params.sortOrder || params.sort_order) queryParams.append('sort_order', params.sortOrder || params.sort_order);

  // GenAIOps correlation filters
  if (params.promptId || params.prompt_id) queryParams.append('prompt_id', params.promptId || params.prompt_id);
  if (params.promptName || params.prompt_name) queryParams.append('prompt_name', params.promptName || params.prompt_name);
  if (params.promptVersion || params.prompt_version) queryParams.append('prompt_version', params.promptVersion || params.prompt_version);
  if (params.datasetId || params.dataset_id) queryParams.append('dataset_id', params.datasetId || params.dataset_id);
  if (params.testCaseId || params.test_case_id) queryParams.append('test_case_id', params.testCaseId || params.test_case_id);
  if (params.environment) queryParams.append('environment', params.environment);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/traces${query ? '?' + query : ''}`);
};

export const getTrace = async (traceId: string): Promise<any> => {
  return fetchAPI(`/genaiops/traces/${traceId}`);
};

export const getTraceGenerations = async (traceId: string): Promise<any> => {
  return fetchAPI(`/genaiops/traces/${traceId}/generations`);
};

export const getTracesTimeseries = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();

  // Prefer ISO dates over hours
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  // Fallback to hours for backward compatibility (only if no dates provided)
  if (!params.start_date && !params.end_date && params.hours) {
    queryParams.append('hours', params.hours);
  }
  if (params.agentName) queryParams.append('agent_name', params.agentName);
  if (params.status) queryParams.append('status', params.status);
  if (params.client_id) queryParams.append('client_id', params.client_id);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/traces/timeseries/hourly${query ? '?' + query : ''}`);
};

export const getSessions = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', String(params.page));
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.offset) queryParams.append('offset', String(params.offset));
  if (params.source) queryParams.append('source', params.source);
  if (params.user_id) queryParams.append('user_id', params.user_id);
  if (params.agent_name) queryParams.append('agent_name', params.agent_name);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/sessions${query ? '?' + query : ''}`);
};

export const getSession = async (sessionId: string): Promise<any> => {
  return fetchAPI(`/genaiops/sessions/${sessionId}`);
};

export const getSessionMessages = async (sessionId: string): Promise<any> => {
  return fetchAPI(`/genaiops/sessions/${sessionId}/messages`);
};

export const deleteTrace = async (traceId: string): Promise<any> => {
  return fetchAPI(`/genaiops/traces/${traceId}`, { method: 'DELETE' });
};

export const bulkDeleteTraces = async (ids: string[]): Promise<any> => {
  return fetchAPI('/genaiops/traces/bulk', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
};

export const deleteSession = async (sessionId: string): Promise<any> => {
  return fetchAPI(`/genaiops/sessions/${sessionId}`, { method: 'DELETE' });
};

export const bulkDeleteSessions = async (ids: string[]): Promise<any> => {
  return fetchAPI('/genaiops/sessions/bulk', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
};

export const bulkAssignTraceProject = async (ids: string[], projectId: string | null): Promise<any> => {
  return fetchAPI('/genaiops/traces/bulk-assign-project', {
    method: 'PATCH',
    body: JSON.stringify({ ids, project_id: projectId }),
  });
};

export const bulkAssignSessionProject = async (ids: string[], projectId: string | null): Promise<any> => {
  return fetchAPI('/genaiops/sessions/bulk-assign-project', {
    method: 'PATCH',
    body: JSON.stringify({ ids, project_id: projectId }),
  });
};

export const assignSessionProject = async (sessionId: string, projectId: string | null): Promise<any> => {
  return fetchAPI(`/genaiops/sessions/${sessionId}/project`, {
    method: 'PATCH',
    body: JSON.stringify({ project_id: projectId }),
  });
};

export const bulkAssignTraceWorkload = async (ids: string[], workloadId: string | null): Promise<any> => {
  return fetchAPI('/genaiops/traces/bulk-assign-workload', {
    method: 'PATCH',
    body: JSON.stringify({ ids, workload_id: workloadId }),
  });
};

export const bulkAssignSessionWorkload = async (ids: string[], workloadId: string | null): Promise<any> => {
  return fetchAPI('/genaiops/sessions/bulk-assign-workload', {
    method: 'PATCH',
    body: JSON.stringify({ ids, workload_id: workloadId }),
  });
};

export const assignSessionWorkload = async (sessionId: string, workloadId: string | null): Promise<any> => {
  return fetchAPI(`/genaiops/sessions/${sessionId}/workload`, {
    method: 'PATCH',
    body: JSON.stringify({ workload_id: workloadId }),
  });
};
